import React from "react";
import { PrintInfoGrid } from "@/components/shared/print";
import { getUploadUrl } from "@/lib/utils";

export interface ProductionOrderPrintTemplateProps {
  document?: any;
  lsx?: any;
  salesOrder?: any;
  orderProducts?: any[];
  config?: any;
  showImages?: boolean;
  isBlankForm?: boolean;
}

export default function ProductionOrderPrintTemplate({
  document,
  lsx: propLsx,
  salesOrder: propSalesOrder,
  orderProducts: propOrderProducts,
  config,
  showImages = true,
  isBlankForm = false,
}: ProductionOrderPrintTemplateProps) {
  const lsx = propLsx || document?.lsx || document || {};
  const salesOrder = propSalesOrder || document?.salesOrder || lsx.salesOrder;
  const orderProducts = propOrderProducts || document?.orderProducts || lsx.orderProducts || [];
  const techSpecs = lsx.techSpecs || {};
  const technicalSpec = lsx.technicalSpec || {};

  const qrUrl = isBlankForm ? "" : (lsx.orderId || lsx.id || "LSX-DEMO");

  const formatDate = (dateString?: string | Date) => {
    if (isBlankForm) return "";
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatNumber = (val?: number | string) => {
    if (isBlankForm) return "";
    if (val === null || val === undefined || val === "") return "—";
    if (isNaN(Number(val))) return String(val);
    return Number(val).toLocaleString("vi-VN");
  };

  // Helper to extract material plans
  const materials = lsx.materialPlans || lsx.materials || lsx.bomItems || [];

  // Helper to extract process routing
  const routingSteps = lsx.routingSteps || [];
  const quyTrinhStr =
    techSpecs.quyTrinhSanXuat ||
    techSpecs.quyTrinh ||
    routingSteps.map((s: any) => s.processName || s.process?.name || s.name).filter(Boolean).join(" , ");

  // Unit detector based on process name
  const getStepUnit = (stepName: string, defaultUnit: string = "Tờ") => {
    const lower = (stepName || "").toLowerCase();
    if (
      lower.includes("dán") ||
      lower.includes("ép túi") ||
      lower.includes("ép 3") ||
      lower.includes("ép 4") ||
      lower.includes("ép gia") ||
      lower.includes("kiểm") ||
      lower.includes("đóng") ||
      lower.includes("giao") ||
      lower.includes("xé") ||
      lower.includes("bế đứt") ||
      lower.includes("bế hộp") ||
      lower.includes("demi")
    ) {
      return "pcs";
    }
    return defaultUnit || "Tờ";
  };

  // Artwork image resolution
  const rawArtworkUrl =
    techSpecs.hinhBaiBinh1 ||
    lsx.hinhBaiBinh1 ||
    lsx.artworkUrl ||
    lsx.printLayouts?.[0]?.fileUrl ||
    lsx.product?.image ||
    lsx.product?.artworkUrl ||
    lsx.product?.thumbnailUrl ||
    "";
  const artworkUrl = rawArtworkUrl ? getUploadUrl(rawArtworkUrl) : "";

  // Helper to extract plates (Single-use CTP) and toolings (Reusable molds)
  const rawToolings = lsx.toolings || [];
  // Ưu tiên dòng khuôn/kẽm của lệnh (có loại khuôn); quan hệ cũ dieCuts/plates chỉ dùng khi lệnh chưa có dòng
  const plateRows = rawToolings.filter((t: any) => t.toolingType === "PLATE" || t.toolingType === "ZINC");
  const moldRows = rawToolings.filter((t: any) => t.toolingType !== "PLATE" && t.toolingType !== "ZINC");
  const plates = plateRows.length > 0 ? plateRows : lsx.plates || [];
  const toolings =
    moldRows.length > 0
      ? moldRows
      : (lsx.dieCuts || []).map((d: any) => ({ ...d, toolingType: "DIE_CUT", moldCode: d.dieCutId || d.code }));

  // Detect printing machine from routing steps (no hardcoded fallback)
  const printStep = routingSteps.find((s: any) => {
    const name = (s.processName || s.process?.name || s.name || "").toLowerCase();
    return name.includes("in") || name.includes("offset");
  });
  const printMachineName = printStep?.assignedMachine || printStep?.machine?.name || printStep?.workCenter || lsx.machineName || "Komori 4 màu";

  // Die cut mold lookup
  // Danh mục mặt hàng của lệnh: từ dòng sản phẩm của chính lệnh (không lấy toàn bộ dòng của đơn hàng bán)
  const poSourceLines: any[] = lsx.productionOrderSources || [];
  const poProductRows = (poSourceLines.length > 0
    ? poSourceLines.map((src: any, idx: number) => {
        const od = src.orderDetail || {};
        const prod = od.product || src.product || {};
        return {
          key: src.id || `src-${idx}`,
          orderCode: od.order?.orderId || src.salesOrderCode || "",
          name: prod.productName || od.productName || src.productName || lsx.productName || `Mặt hàng ${idx + 1}`,
          code: prod.productCode || "",
          specs: od.specs || "",
          unit: od.unit || prod.unit || "Cái",
          ups: Number(src.pcsPerSheet) || 0,
          qtyOrder: Number(src.quantity) || 0,
          stock: Number(src.inventoryQty) || 0,
          qtyPlan: Number(src.plannedQty) || Number(src.quantity) || 0,
          notes: [src.finishingNotes, src.deliveryNotes].filter(Boolean).join(" · "),
        };
      })
    : [
        {
          key: "lsx",
          orderCode: salesOrder?.orderId || lsx.salesOrder?.orderId || "",
          name: lsx.productName || lsx.product?.productName || "—",
          code: lsx.product?.productCode || "",
          specs: "",
          unit: lsx.product?.unit || "Cái",
          ups: Number(lsx.ups) || 0,
          qtyOrder: Number(lsx.qtyOrder) || 0,
          stock: 0,
          qtyPlan: Number(lsx.qtyPlan) || Number(lsx.qtyOrder) || 0,
          notes: "",
        },
      ]);

  // Thông số gia công lấy từ các bảng inline của lệnh (vật tư, khuôn/kẽm, công đoạn, bài bình, dòng sản phẩm).
  // Lệnh cũ: dự phòng techSpecs.finishing.* rồi tới khóa cấp trên cùng.
  const legacyFin: any = { ...(techSpecs || {}), ...((techSpecs as any)?.finishing || {}) };
  const textOf = (v: any) => String(v ?? "").trim();
  const joinNotes = (vals: any[]) => Array.from(new Set(vals.map(textOf).filter(Boolean))).join(" · ");
  const matName = (m: any) => `${m.materialCode || m.material?.materialId || m.material?.code || ""} ${m.materialName || m.material?.name || m.name || ""} ${m.specification || ""}`;
  const filmLine = materials.find((m: any) => /m[aà]ng|bopp|opp|film/i.test(matName(m)));
  const boiLines = materials.filter((m: any) => /s[oó]ng|b[oồ]i|duplex|kraft|carton/i.test(matName(m)));
  const stepReq = (re: RegExp) =>
    joinNotes(
      routingSteps
        .filter((st: any) => re.test(`${st.name || ""} ${st.processName || st.process?.name || ""}`))
        .map((st: any) => st.technicalRequirements),
    );
  const findTool = (types: string[]) => rawToolings.find((t: any) => types.includes(t.toolingType || t.type));
  const dieRow = findTool(["DIE_CUT"]);
  const foilRow = findTool(["FOIL_STAMP", "FOIL"]);
  const sources: any[] = lsx.productionOrderSources || lsx.orderSources || [];
  const fin = {
    maMang: filmLine ? textOf(`${filmLine.materialCode || filmLine.material?.materialId || ""} ${filmLine.materialName || filmLine.material?.name || ""}`) : legacyFin.maMang,
    khoMang: filmLine?.suppliedSize || filmLine?.specification || legacyFin.khoMang,
    slMangM: filmLine ? filmLine.qtyRequired ?? filmLine.plannedQuantity : legacyFin.slMangM,
    boi: boiLines.length ? joinNotes(boiLines.map((m: any) => m.materialName || m.material?.name || m.specification)) : legacyFin.boi,
    dienTichEpKim: foilRow?.areaCm2 ? Number(foilRow.areaCm2) : legacyFin.dienTichEpKim,
    kieuBe: dieRow?.dieCutStyle ? textOf(dieRow.dieCutStyle).replace(/^Khác:\s*/, "") : legacyFin.kieuBe,
    khuonBe: legacyFin.khuonBe,
    ghiChuBe: dieRow?.notes || legacyFin.ghiChuBe,
    trangPhu: stepReq(/tr[aá]ng|ph[uủ]|uv|vecni|vanish/i) || legacyFin.trangPhu,
    chietQuang: stepReq(/chi[eế]t quang/i) || legacyFin.chietQuang,
    beNoi: stepReq(/b[eế] n[oổ]i|d[aậ]p n[oổ]i|th[uú]c n[oổ]i/i) || legacyFin.beNoi,
    xe: stepReq(/x[eé]/i) || legacyFin.xe,
    dan: stepReq(/d[aá]n/i) || legacyFin.dan,
    catThanhPham: stepReq(/c[aắ]t (th[aà]nh ph[aẩ]m|tp)|x[eẻ] 4/i) || legacyFin.catThanhPham,
    ghiChuBinhBai: joinNotes((lsx.printLayouts || []).map((l: any) => l.notes)) || legacyFin.ghiChuBinhBai,
    ghiChuThanhPham: joinNotes(sources.map((x: any) => x.finishingNotes)) || legacyFin.ghiChuThanhPham,
    ghiChuGiaoHang: joinNotes(sources.map((x: any) => x.deliveryNotes)) || legacyFin.ghiChuGiaoHang,
  };

  const dieCutTool = toolings.find((t: any) => t.toolingType === "DIE_CUT" || t.type === "DIE_CUT");
  const dieCutCode = dieCutTool?.moldCode || dieCutTool?.dieCutId || dieCutTool?.code || fin.khuonBe || technicalSpec.dieCut?.code || "Theo bài in";
  const dieCutLocation = dieCutTool?.location || dieCutTool?.dieCut?.location || "KHO-KHUON/K01";

  // Foil stamp mold lookup
  const foilTool = toolings.find((t: any) => t.toolingType === "FOIL_STAMP" || t.type === "FOIL_STAMP");
  const foilLocation = foilTool?.location || foilTool?.dieCut?.location || "";

  // Emboss mold lookup
  const embossTool = toolings.find((t: any) => t.toolingType === "EMBOSS_DIE" || t.type === "EMBOSS_DIE");
  const embossLocation = embossTool?.location || embossTool?.dieCut?.location || "";

  return (
    <div className="kh-print-wrapper w-full bg-white text-black font-serif">
      {/* ========== TRANG 1: LỆNH SẢN XUẤT CHÍNH (A4 PORTRAIT) ========== */}
      <div
        id="print-area"
        className="kh-print-doc w-full bg-white text-black p-[8mm] font-serif mx-auto max-w-[190mm] min-h-[270mm] text-[9.5pt] leading-tight print:p-0 print:m-0"
        style={{ fontFamily: '"Times New Roman", Times, serif' }}
      >
        {/* ========== HEADER COMPANY & ISO BADGE ========== */}
        {/* ========== HEADER COMPANY & TITLE (CENTERED, NO QR) ========== */}
        <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-2">
          <div className="w-[100px] flex-shrink-0 flex items-center">
            {config?.logoUrl ? (
              <img
                src={config.logoUrl}
                alt="Logo"
                className="h-[52px] max-w-[100px] object-contain"
              />
            ) : (
              <div className="h-[52px] w-[90px] flex items-center justify-center border border-slate-400 bg-slate-50 text-slate-600 font-bold text-[11px]">
                APV PACK
              </div>
            )}
          </div>

          <div className="text-center flex-1 px-2">
            <div className="text-[19px] font-bold uppercase tracking-wide">
              LỆNH SẢN XUẤT {isBlankForm ? "" : <span className="text-indigo-900">{lsx.orderId ? `| ${lsx.orderId}` : ""}</span>}
            </div>
            <div className="flex justify-center items-center gap-3 mt-1 text-[10pt] font-semibold text-slate-800">
              <span>Ngày lập: <span>{isBlankForm ? "....../....../202..." : formatDate(lsx.createdAt)}</span></span>
              <span>•</span>
              <span>{isBlankForm ? "Sản xuất mới" : (lsx.loaiDon || lsx.deploymentModel || "Sản xuất mới")}</span>
            </div>
          </div>

          <div className="w-[100px] flex-shrink-0 text-right text-[9pt] font-medium text-slate-600">
            <div>BM-SX-01</div>
            
          </div>
        </div>

        {/* ========== SECTION A: THÔNG TIN ĐƠN HÀNG (CLEAN BORDERLESS TABS) ========== */}
        <div className="mb-2 pb-2 border-b border-slate-300 print:break-inside-avoid">
          <div className="font-bold text-[10.5pt] mb-1.5 uppercase text-slate-900 flex items-center gap-2">
            <span className="w-1.5 h-3.5 bg-indigo-900 inline-block rounded-xs"></span>
            A. THÔNG TIN ĐƠN HÀNG
          </div>
          <div className="flex gap-4 items-start">
            <div className="flex-1">
              <PrintInfoGrid
                className="mb-0"
                labelWidth="w-[105px]"
                leftColumn={[
                  {
                    label: "Khách hàng",
                    value: isBlankForm ? "" : (salesOrder?.customer?.name || lsx.customerName || lsx.customer?.name || "—"),
                    valueClassName: "text-indigo-950 font-bold",
                  },
                  {
                    label: "Hạn giao hàng",
                    value: isBlankForm ? "" : formatDate(lsx.deadline || salesOrder?.deliveryDeadline || salesOrder?.deliveryDate),
                    valueClassName: "text-amber-900 font-bold",
                  },
                ]}
                rightColumn={[
                  {
                    label: "Mã LSX / ĐH",
                    value: lsx.orderId || "—",
                    valueClassName: "font-mono text-indigo-900 font-bold",
                  },
                  {
                    label: "Tổng SL",
                    value: isBlankForm ? "" : `${formatNumber(lsx.qtyPlan || lsx.qtyOrder)} Cái`,
                    valueClassName: "text-indigo-900 font-bold",
                  },
                ]}
              />
            </div>


            {/* Thumbnail Bài Bình */}
            {showImages && !isBlankForm && artworkUrl && (
              <div className="w-[110px] h-[80px] border border-slate-400 rounded overflow-hidden bg-slate-50 shrink-0 flex items-center justify-center p-0.5">
                <img
                  src={artworkUrl}
                  alt="Bài bình"
                  className="w-full h-full object-contain"
                  onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                />
              </div>
            )}
          </div>
          {/* Trường dài (quy trình, ghi chú) gộp ô toàn chiều ngang để không bị xuống dòng vụn */}
          <PrintInfoGrid
            className="mt-1.5 mb-0"
            labelWidth="w-[105px]"
            fullWidthItems={[
              { label: "Quy trình SX", value: isBlankForm ? "" : (quyTrinhStr || "In Offset, Cán màng, Bế, Dán thành phẩm"), colSpan: 2 },
              {
                label: "Ghi chú bình bài",
                value: isBlankForm ? "" : (fin.ghiChuBinhBai || lsx.layoutNotes || "—"),
                valueClassName: "italic text-slate-700 font-normal",
                colSpan: 2,
              },
            ]}
          />
        </div>

        {/* ========== SECTION B: DANH MỤC MẶT HÀNG CỦA LỆNH ========== */}
        <div className="mb-2 print:break-inside-avoid" data-testid="print-po-products">
          <div className="font-bold text-[10.5pt] bg-slate-100 px-1.5 py-1 border border-b-0 border-slate-400 uppercase">
            B. DANH MỤC MẶT HÀNG
          </div>
          <table className="w-full border-collapse border border-slate-400 text-[9pt]">
            <thead>
              <tr className="bg-slate-100 font-bold text-center text-[8.5pt]">
                <th className="border border-slate-400 p-1 w-[5%] whitespace-nowrap">STT</th>
                <th className="border border-slate-400 p-1 w-[16%] whitespace-nowrap">Mã ĐH</th>
                <th className="border border-slate-400 p-1 text-left w-[26%] whitespace-nowrap">Sản Phẩm / Quy Cách</th>
                <th className="border border-slate-400 p-1 w-[6%] whitespace-nowrap">ĐVT</th>
                <th className="border border-slate-400 p-1 w-[8%] whitespace-nowrap">Con/Tờ</th>
                <th className="border border-slate-400 p-1 w-[9%] whitespace-nowrap">SL Đặt</th>
                <th className="border border-slate-400 p-1 w-[7%] whitespace-nowrap">Tồn</th>
                <th className="border border-slate-400 p-1 w-[9%] whitespace-nowrap">SL KH</th>
                <th className="border border-slate-400 p-1 text-left w-[14%] whitespace-nowrap">Ghi Chú</th>
              </tr>
            </thead>
            <tbody>
              {isBlankForm
                ? [1, 2, 3, 4].map((idx) => (
                    <tr key={idx}>
                      <td className="border border-slate-400 p-1 text-center h-6">{idx}</td>
                      {Array.from({ length: 8 }).map((_, i) => (
                        <td key={i} className="border border-slate-400 p-1"></td>
                      ))}
                    </tr>
                  ))
                : poProductRows.map((r, idx) => (
                    <tr key={r.key}>
                      <td className="border border-slate-400 p-1 text-center font-medium">{idx + 1}</td>
                      <td className="border border-slate-400 p-1 text-[8pt] whitespace-nowrap text-center">{r.orderCode || "—"}</td>
                      <td className="border border-slate-400 p-1 leading-tight">
                        <div className="font-bold">{r.name}</div>
                        {(r.code || r.specs) && (
                          <div className="text-[8pt] text-slate-600">{[r.code, r.specs].filter(Boolean).join(" · ")}</div>
                        )}
                      </td>
                      <td className="border border-slate-400 p-1 text-center whitespace-nowrap">{r.unit}</td>
                      <td className="border border-slate-400 p-1 text-center whitespace-nowrap">{r.ups || "—"}</td>
                      <td className="border border-slate-400 p-1 text-right font-bold whitespace-nowrap">{formatNumber(r.qtyOrder)}</td>
                      <td className="border border-slate-400 p-1 text-right whitespace-nowrap">{formatNumber(r.stock)}</td>
                      <td className="border border-slate-400 p-1 text-right font-bold text-indigo-900 whitespace-nowrap">{formatNumber(r.qtyPlan)}</td>
                      <td className="border border-slate-400 p-1 text-slate-700 text-[8.5pt]">{r.notes}</td>
                    </tr>
                  ))}
            </tbody>
            {!isBlankForm && poProductRows.length > 1 && (
              <tfoot>
                <tr className="bg-slate-100 font-bold">
                  <td colSpan={5} className="border border-slate-400 p-1 text-right">TỔNG CỘNG ({poProductRows.length} mặt hàng):</td>
                  <td className="border border-slate-400 p-1 text-right whitespace-nowrap">{formatNumber(poProductRows.reduce((a, r) => a + r.qtyOrder, 0))}</td>
                  <td className="border border-slate-400 p-1 text-right whitespace-nowrap">{formatNumber(poProductRows.reduce((a, r) => a + r.stock, 0))}</td>
                  <td className="border border-slate-400 p-1 text-right text-indigo-950 whitespace-nowrap">{formatNumber(poProductRows.reduce((a, r) => a + r.qtyPlan, 0))}</td>
                  <td className="border border-slate-400 p-1"></td>
                </tr>
              </tfoot>
            )}
          </table>
          <div className="text-[9pt] border border-t-0 border-slate-400 px-1.5 py-1">
            <span className="font-bold">Ghi chú chung:</span>{" "}
            <span className="italic text-slate-800">{isBlankForm ? "..................................................................................." : (techSpecs?.notes || techSpecs?.ghiChu || lsx.notes || "—")}</span>
          </div>
        </div>

        {/* ========== SECTION B: CHI TIẾT VẬT TƯ & IN ẤN ========== */}
        <div className="mb-2 print:break-inside-avoid">
          <div className="font-bold text-[10.5pt] bg-slate-100 px-1.5 py-1 border border-b-0 border-slate-400 uppercase">
            C. CHI TIẾT VẬT TƯ &amp; IN ẤN
          </div>
          <table className="w-full border-collapse border border-slate-400 text-[9pt]">
            <thead>
              <tr className="bg-slate-100 font-bold text-center">
                <th className="border border-slate-400 p-1 w-8 whitespace-nowrap">STT</th>
                <th className="border border-slate-400 p-1 text-left">Mã Vật Tư / Tên NVL</th>
                <th className="border border-slate-400 p-1 w-20">SL Mua (Tờ)</th>
                <th className="border border-slate-400 p-1 w-20">Khổ in (cm)</th>
                <th className="border border-slate-400 p-1 w-20">SL In (Tờ)</th>
                <th className="border border-slate-400 p-1 w-16">Bù hao</th>
                <th className="border border-slate-400 p-1 w-28">Kiểu / Màu in</th>
              </tr>
            </thead>
            <tbody>
              {isBlankForm ? (
                [1, 2, 3, 4, 5, 6].map((idx) => (
                  <tr key={idx}>
                    <td className="border border-slate-400 p-1 text-center h-6">{idx}</td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1"></td>
                  </tr>
                ))
              ) : materials && materials.length > 0 ? (
                materials.map((m: any, idx: number) => (
                  <tr key={m.id || idx}>
                    <td className="border border-slate-400 p-1 text-center font-medium">{idx + 1}</td>
                    <td className="border border-slate-400 p-1 font-semibold">
                      {m.plannedMaterial?.materialId || m.material?.code || m.materialCode || m.specification || `Vật tư ${idx + 1}`}
                      {(m.plannedMaterial?.name || m.material?.name) ? ` (${m.plannedMaterial?.name || m.material?.name})` : ""}
                    </td>
                    <td className="border border-slate-400 p-1 text-center font-bold">
                      {formatNumber(m.slMuaTo || m.purchasedQty || m.standardQuantity || m.quantity)}
                    </td>
                    <td className="border border-slate-400 p-1 text-center font-mono">
                      {m.printingPaperSize || m.purchasedPaperSize || m.sheetSize || lsx.sheetSize || "—"}
                    </td>
                    <td className="border border-slate-400 p-1 text-center font-bold text-indigo-900">
                      {formatNumber(m.standardQuantity || m.qtyRequired || m.slInTo || lsx.runSheetsNeeded)}
                    </td>
                    <td className="border border-slate-400 p-1 text-center text-amber-800 font-medium">
                      {formatNumber(m.allowance || m.wasteQuantity || 0)}
                    </td>
                    <td className="border border-slate-400 p-1 text-center">
                      {m.kieuIn || m.specification || lsx.technicalSpec?.printColors || "4 Màu Offset"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="border border-slate-400 p-2 text-center text-slate-500 italic">
                    Không có thông tin vật tư chi tiết
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* ========== KẾ HOẠCH BẢN KẼM CTP (từ dòng kẽm của lệnh, không còn mặc định 4 bản CMYK) ========== */}
          {!isBlankForm && (
            <div className="text-[8.5pt] bg-slate-50/70 border border-t-0 border-slate-400 px-1.5 py-1 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-bold text-amber-950 uppercase tracking-tight">Kế hoạch kẽm CTP:</span>
                {plates.length > 0 ? (
                  <>
                    <span className="font-bold text-indigo-900">{plates.reduce((a: number, p: any) => a + (Number(p.quantity) || 1), 0)} bản</span>
                    <span className="text-slate-700">
                      ({plates
                        .map((p: any, i: number) => p.plateColor || p.colorName || p.notes?.match(/Màu:\s*([^|]+)/i)?.[1]?.trim() || p.processName || `Kẽm ${i + 1}`)
                        .join(", ")})
                    </span>
                  </>
                ) : (
                  <span className="italic text-slate-600">Chưa lập kế hoạch kẽm</span>
                )}
              </div>
              {plates.length > 0 && (
                <div className="flex items-center gap-3 text-slate-800">
                  <span>
                    <span className="font-bold">Khổ kẽm:</span>{" "}
                    <span className="font-mono font-semibold">
                      {plates[0]?.plateSize || plates[0]?.notes?.match(/Khổ:\s*([^|]+)/i)?.[1]?.trim() || "—"}
                    </span>
                  </span>
                  <span>
                    <span className="font-bold">Máy in:</span>{" "}
                    <span className="font-semibold text-indigo-950">{printMachineName}</span>
                  </span>
                  <span>
                    <span className="font-bold">Nguồn:</span>{" "}
                    <span className="font-medium">
                      {(plates[0]?.plateSource || plates[0]?.source) === "OUTSOURCE" ? "Gia công ngoài" : "Nội bộ CTP"}
                    </span>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ========== SECTION D: IN ẤN - GIA CÔNG - THÀNH PHẨM ========== */}
        <div className="mb-2 print:break-inside-avoid">
          <div className="font-bold text-[10.5pt] bg-slate-100 px-1.5 py-1 border border-b-0 border-slate-400 uppercase">
            D. IN ẤN - GIA CÔNG - THÀNH PHẨM
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[9pt] border border-slate-400 px-1.5 py-1">
            <div>
              <span className="font-bold">Cán màng:</span>{" "}
              <span>
                {isBlankForm ? "..................................................." : (fin.maMang || technicalSpec.laminationType || (technicalSpec.hasLamination ? "BOPP" : "—"))}
                {!isBlankForm && fin.khoMang ? ` / Khổ: ${fin.khoMang}cm` : ""}
                {!isBlankForm && fin.slMangM ? ` / SL: ${formatNumber(fin.slMangM)}m` : ""}
              </span>
            </div>
            <div>
              <span className="font-bold">Ép kim / nhũ:</span>{" "}
              <span>
                {isBlankForm
                  ? "..................................................."
                  : `${fin.dienTichEpKim || technicalSpec.foilArea ? `DT: ${fin.dienTichEpKim || technicalSpec.foilArea} cm² | ` : ""}${foilTool ? `Khuôn: ${foilTool.moldCode || foilTool.toolingId}${foilLocation ? ` (Kệ: ${foilLocation})` : ""} | ` : ""}Mã nhũ: ${techSpecs.maNhu || technicalSpec.foilColor || "—"}`}
              </span>
            </div>
            <div>
              <span className="font-bold">Tráng phủ:</span>{" "}
              <span>{isBlankForm ? "..................................................." : (fin.trangPhu || technicalSpec.coating || "—")}</span>
            </div>
            <div>
              <span className="font-bold">Chiết quang:</span>{" "}
              <span>{isBlankForm ? "..................................................." : (fin.chietQuang || "—")}</span>
            </div>
            <div>
              <span className="font-bold">Bồi:</span>{" "}
              <span>{isBlankForm ? "..................................................." : (fin.boi || (technicalSpec.hasFlute ? `Sóng ${technicalSpec.fluteType || "E"}` : "—"))}</span>
            </div>
            <div>
              <span className="font-bold">Bế nổi:</span>{" "}
              <span>
                {isBlankForm
                  ? "..................................................."
                  : `${embossTool ? `Có khuôn (${embossTool.moldCode || embossTool.toolingId}${embossLocation ? ` - Kệ: ${embossLocation}` : ""})` : (fin.beNoi || (technicalSpec.hasEmboss ? "Có bế nổi" : "—"))}`}
              </span>
            </div>
            <div>
              <span className="font-bold">Kiểu bế:</span>{" "}
              <span>{isBlankForm ? "..................................................." : (fin.kieuBe || "Bế đứt")}</span>
            </div>
            <div>
              <span className="font-bold">Khuôn bế:</span>{" "}
              <span className="font-semibold text-slate-900">
                {isBlankForm
                  ? "..................................................."
                  : `${dieCutCode}${dieCutLocation ? ` (Kệ: ${dieCutLocation})` : ""}`}
              </span>
            </div>
            <div>
              <span className="font-bold">Xé:</span>{" "}
              <span>{isBlankForm ? "..................................................." : (fin.xe || "—")}</span>
            </div>
            <div>
              <span className="font-bold">Dán:</span>{" "}
              <span>{isBlankForm ? "..................................................." : (fin.dan || technicalSpec.glueType || "Dán máy")}</span>
            </div>
            <div className="col-span-2">
              <span className="font-bold">Cắt TP &amp; Ghi chú bế:</span>{" "}
              <span>{isBlankForm ? "..........................................................................................................................." : `${fin.catThanhPham || "Xén đúng vạch"} | ${fin.ghiChuBe || ""}`}</span>
            </div>
            <div className="col-span-2 text-indigo-950 italic">
              <span className="font-bold not-italic text-black">Ghi chú TP:</span>{" "}
              <span>{isBlankForm ? "..........................................................................................................................." : (fin.ghiChuThanhPham || technicalSpec.packagingNotes || "Đóng gói theo tiêu chuẩn")}</span>
            </div>
          </div>
        </div>

        {/* ========== SECTION D: KẾ HOẠCH SẢN XUẤT F0 ========== */}
        <div className="mb-2 print:break-inside-avoid">
          <div className="font-bold text-[10.5pt] bg-slate-100 px-1.5 py-1 border border-b-0 border-slate-400 uppercase">
            E. KẾ HOẠCH SẢN XUẤT (BẢNG F0 THỰC THI)
          </div>
          <table className="w-full border-collapse border border-slate-400 text-[8.5pt]">
            <thead>
              <tr className="bg-slate-100 font-bold text-center">
                <th className="border border-slate-400 p-1 w-8 whitespace-nowrap">STT</th>
                <th className="border border-slate-400 p-1 text-left">Công Đoạn / Mô Tả</th>
                <th className="border border-slate-400 p-1 w-10">ĐVT</th>
                <th className="border border-slate-400 p-1 w-14">SL YC</th>
                <th className="border border-slate-400 p-1 w-12">Bù hao</th>
                <th className="border border-slate-400 p-1 w-12">SL Đạt</th>
                <th className="border border-slate-400 p-1 w-12">SL Hư</th>
                <th className="border border-slate-400 p-1 w-24">NV / Tổ Máy</th>
                <th className="border border-slate-400 p-1 text-left">Ghi Chú</th>
              </tr>
            </thead>
            <tbody>
              {isBlankForm ? (
                [1, 2, 3, 4, 5, 6].map((idx) => (
                  <tr key={idx}>
                    <td className="border border-slate-400 p-1 text-center h-6">{idx}</td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1 text-center">Tờ</td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1"></td>
                    <td className="border border-slate-400 p-1"></td>
                  </tr>
                ))
              ) : routingSteps && routingSteps.length > 0 ? (
                routingSteps.map((s: any, idx: number) => {
                  const pName = s.processName || s.process?.name || s.name || `Công đoạn ${idx + 1}`;
                  const stepUnit = getStepUnit(pName, s.dvt || s.unit);
                  return (
                    <tr key={s.id || idx}>
                      <td className="border border-slate-400 p-1 text-center font-bold">{idx + 1}</td>
                      <td className="border border-slate-400 p-1 font-medium">{pName}</td>
                      <td className="border border-slate-400 p-1 text-center font-bold text-indigo-900">{stepUnit}</td>
                      <td className="border border-slate-400 p-1 text-center font-bold">
                        {formatNumber(s.targetQuantity || s.standardQuantity || lsx.qtyPlan)}
                      </td>
                      <td className="border border-slate-400 p-1 text-center text-amber-800">
                        {formatNumber(s.wasteAllowance || s.plannedSetupWaste || 0)}
                      </td>
                      <td className="border border-slate-400 p-1 text-center font-bold text-emerald-800">
                        {formatNumber(s.actualQuantity || s.actualQtyOk)}
                      </td>
                      <td className="border border-slate-400 p-1 text-center text-rose-700">
                        {formatNumber(s.scrapQuantity || s.actualQtyNg)}
                      </td>
                      <td className="border border-slate-400 p-1 text-center text-slate-800">
                        {s.operator?.fullName || s.operatorName || s.machine?.name || s.workCenter || "—"}
                      </td>
                      <td className="border border-slate-400 p-1 text-slate-600 text-[8pt]">{s.notes || ""}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="border border-slate-400 p-2 text-center text-slate-500 italic">
                    Chưa lập quy trình kế hoạch sản xuất
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ========== FOOTER / SIGNATURES ========== */}
        <div className="mt-4 flex justify-around text-center text-[9.5pt] print:break-inside-avoid">
          <div>
            <div className="font-bold mb-12">Người Lập Phiếu</div>
            <div className="font-semibold text-slate-900">
              {isBlankForm ? "..................................." : (lsx.creatorName || lsx.createdUser?.fullName || "Kế Hoạch SX")}
            </div>
          </div>
          <div>
            <div className="font-bold mb-12">Người Duyệt (Quản Đốc)</div>
            <div className="font-semibold text-slate-900">
              {isBlankForm ? "..................................." : (lsx.approverName || "Quản Đốc Xưởng")}
            </div>
          </div>
        </div>
      </div>

      {/* ========== TRANG 2: ĐÍNH KÈM HÌNH ẢNH BÀI IN / BÀI BÌNH (FULL PAGE ARTWORK) ========== */}
      {showImages && !isBlankForm && artworkUrl && (
        <div
          className="kh-print-doc full-page-artwork w-full bg-white text-black p-[8mm] font-serif mx-auto max-w-[190mm] min-h-[270mm] text-[10pt] print:break-before-page"
          style={{ fontFamily: '"Times New Roman", Times, serif', pageBreakBefore: "always" }}
        >
          <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-4">
            <div className="font-bold text-[12pt] uppercase">
              BẢN VẼ BÀI IN / BÀI BÌNH CHI TIẾT
            </div>
            <div className="font-mono text-[10pt] font-bold text-indigo-900">
              LSX: {lsx.orderId || lsx.id} | Khổ in: {lsx.sheetSize || "—"}
            </div>
          </div>
          <div className="w-full h-[230mm] flex items-center justify-center border border-slate-300 rounded p-2 bg-slate-50">
            <img
              src={artworkUrl}
              alt="Bản vẽ bài in"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}
